import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, MapPin, ArrowRight, Phone, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const successStories = [
  {
    id: 1,
    businessName: 'Spruced Pup Lodge',
    owners: 'Two of our very first students',
    location: 'Thornham, Royton',
    instagram: 'sprucedpup',
    phone: null,
    mainImage: 'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/peutkuti_IMG_6002.jpeg',
    galleryImages: [
      'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/4y7t10it_IMG_6004.jpeg',
      'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/n2ruqb6c_IMG_6003.jpeg',
    ],
    story: `These two lovely ladies have truly followed their hearts. After sadly losing their beloved Eddie too soon, they wanted to find something that brought meaning, joy, and purpose back into their lives — and what better way than spending their days surrounded by dogs?

Because life is far too short to be anything other than happy. You never know what's around the corner.

During their time on the course, they worked tirelessly, showed incredible dedication, and soaked up every bit of knowledge we shared. Watching them grow in confidence and skill was an absolute pleasure — we feel so privileged that they chose us to train with.

This is exactly what can happen when you believe in yourself and take that leap toward your dreams.`,
    highlight: 'Their family-run, home-based salon is now open for bookings, and we couldn\'t be prouder!',
    services: null,
  },
  {
    id: 2,
    businessName: 'Hyena Shed Dog Grooming',
    owners: 'Tanya',
    location: 'Glossop',
    instagram: null,
    phone: '07570195408',
    mainImage: 'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/p1dxjbfp_IMG_6010.jpeg',
    galleryImages: [
      'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/a1eehccf_IMG_6013.jpeg',
      'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/rdlgpi6u_IMG_6011.jpeg',
    ],
    story: `Tanya completed our November course, and from the very start she showed enthusiasm, dedication and was amazing with all the dogs. She worked so hard throughout her training, soaking up every part of the course and putting her heart into everything she did.

Her brand-new setup perfectly reflects her calm, caring approach to grooming.

Huge congratulations, Tanya — you should be so proud of what you've achieved. We can't wait to see your business continue to grow!`,
    highlight: 'We\'re so proud to see Tanya has now gone on to open her very own home-based grooming salon!',
    services: null,
  },
  {
    id: 3,
    businessName: "Amy's Grooms",
    owners: 'Amy',
    location: 'Reddish',
    instagram: 'amyswalkies1',
    phone: null,
    mainImage: 'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/hv3dqteb_IMG_6006.jpeg',
    galleryImages: [
      'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/pqkon1ax_IMG_6008.jpeg',
      'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/emgmga3v_IMG_6007.jpeg',
    ],
    story: `Amy joined us on our first November course and truly gave it her all. Already running a successful dog walking business in the Reddish area — Amy's Walkies — she decided it was time to take the next step and expand her services into dog grooming.

Throughout the course, Amy showed incredible focus, passion, and commitment. She took on every challenge with a smile and a "let's do this" attitude, and it really shows in the progress she made. From the moment she started, Amy threw herself into practicing, is never afraid to ask questions and is always looking for ways to grow and get better.

Now she's gone and done it... her brand new home grooming salon is officially up and running! And honestly it looks absolutely gorgeous. A perfect little haven for pups to enjoy their pamper time.

Huge congratulations Amy! We're so excited to see where this new chapter takes you. Your hard work is paying off already!`,
    highlight: "Amy's brand new home grooming salon is officially up and running — and it looks absolutely gorgeous!",
    services: null,
  },
];

const SuccessStoriesPage = () => {
  return (
    <div className="min-h-screen bg-cream-50">
      <SEO 
        title="Success Stories - Dog Grooming Graduates Manchester"
        description="Read inspiring success stories from our Manchester dog grooming academy graduates. See how they turned their passion into thriving businesses after training with us in Greater Manchester."
        keywords="dog grooming success stories Manchester, dog grooming graduates Manchester, become a dog groomer Manchester, dog grooming career Manchester, start dog grooming business Manchester, dog grooming academy graduates"
      />
      {/* Hero Section */}
      <section className="bg-white !pt-[80px] !pb-[40px]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block bg-sage-100 text-sage-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Real Stories, Real Success
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-sage-900">
              Success Stories
            </h1>
            <p className="text-lg leading-relaxed text-sage-700">
              Meet our incredible graduates who have turned their passion into thriving businesses. 
              These inspiring stories show what's possible when you believe in yourself and take the leap.
            </p>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {successStories.map((story, index) => (
            <div key={story.id} className="max-w-6xl mx-auto mb-20 last:mb-0">
              {/* Story Header */}
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-3">
                  {story.businessName}
                </h2>
                <div className="flex flex-wrap items-center justify-center gap-4 text-sage-600">
                  <div className="flex items-center gap-1">
                    <MapPin size={18} />
                    <span>{story.location}</span>
                  </div>
                </div>
              </div>

              {/* Main Image */}
              <div className="mb-8">
                <img
                  src={story.mainImage}
                  alt={`${story.businessName} exterior`}
                  className="w-full max-w-3xl mx-auto rounded-2xl shadow-xl"
                />
              </div>

              {/* Highlight Quote */}
              <div className="max-w-3xl mx-auto mb-10">
                <div className="bg-gradient-to-br from-sage-50 to-sage-100 rounded-2xl p-8 text-center">
                  <p className="text-xl md:text-2xl font-semibold text-sage-900 italic">
                    "{story.highlight}"
                  </p>
                </div>
              </div>

              {/* Gallery */}
              <div className="flex flex-wrap justify-center gap-4 mb-10">
                {story.galleryImages.map((img, imgIndex) => (
                  <img
                    key={imgIndex}
                    src={img}
                    alt={`${story.businessName} - Image ${imgIndex + 1}`}
                    className="w-full sm:w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] max-w-sm aspect-square object-cover rounded-xl shadow-md hover:shadow-lg transition-shadow"
                  />
                ))}
              </div>

              {/* Story Text */}
              <div className="max-w-3xl mx-auto">
                <div className="prose prose-lg text-sage-700">
                  {story.story.split('\n\n').map((paragraph, pIndex) => (
                    <p key={pIndex} className="mb-4 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* Services List */}
                {story.services && (
                  <div className="mt-8 bg-sage-50 rounded-xl p-6">
                    <h4 className="font-bold text-sage-900 mb-4">{story.businessName} offers:</h4>
                    <div className="space-y-2">
                      {story.services.map((service, sIndex) => (
                        <div key={sIndex} className="flex items-center gap-2">
                          <Check className="text-sage-600 flex-shrink-0" size={18} />
                          <span className="text-sage-700">{service}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Divider for multiple stories */}
              {index < successStories.length - 1 && (
                <div className="border-t border-sage-200 mt-16"></div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-sage-50 to-sage-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
              Ready to Write Your Own Success Story?
            </h2>
            <p className="text-lg text-sage-700 mb-8">
              Join our CPD Accredited dog grooming course and take the first step 
              toward your dream career.
            </p>
            <Link to="/academy">
              <Button size="lg" className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-primary/90 h-10 bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 text-sm font-medium gap-2 px-8 rounded-md shadow-lg text-white">
                Learn About Our Academy <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SuccessStoriesPage;
